import { loginSchema } from '@hemmafixa/shared';
import { Button, FormField, Input, PasswordInput } from '@hemmafixa/ui';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useI18n } from '@/i18n/context';
import { afterLogin } from './access';
import { useLogin, useSession } from './api';
import { authErrorMessage } from './auth-errors';
import { AuthPage } from './auth-page';

export function LoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const session = useSession();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  if (session.data) return <Navigate to={afterLogin(session.data, next)} replace />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const fields = parsed.error.flatten().fieldErrors;
      setErrors({
        email: fields.email ? t.auth.emailInvalid : undefined,
        password: fields.password ? t.auth.passwordRequired : undefined,
      });
      return;
    }
    setErrors({});
    login.mutate(parsed.data, {
      onSuccess: ({ user }) => navigate(afterLogin(user, next), { replace: true }),
    });
  };

  return (
    <AuthPage
      title={t.auth.loginTitle}
      description={t.auth.loginDescription}
      footer={
        <span className="grid gap-2">
          <span>{t.auth.noAccount}</span>
          <Link
            to="/provider-signup"
            className="font-semibold text-foreground underline-offset-4 hover:underline"
          >
            {t.auth.providerLink}
          </Link>
        </span>
      }
    >
      <form className="grid gap-4" onSubmit={submit} noValidate>
        {login.isError && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm font-medium text-danger">
            {authErrorMessage(login.error, t)}
          </p>
        )}
        <FormField label={t.auth.email} error={errors.email} required>
          <Input
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </FormField>
        <FormField label={t.auth.password} error={errors.password} required>
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            showLabel={t.auth.showPassword}
            hideLabel={t.auth.hidePassword}
          />
        </FormField>
        {/* The email goes along in router state, not the URL, so it stays out of history and logs */}
        <Link
          to="/forgot-password"
          state={{ email }}
          className="justify-self-start text-sm font-semibold underline-offset-4 hover:underline"
        >
          {t.auth.forgotLink}
        </Link>
        <Button type="submit" size="lg" loading={login.isPending}>
          {t.auth.login}
        </Button>
      </form>
    </AuthPage>
  );
}
